import { useState, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MeshGradient } from "../components/AnimatedBackground";
import {
  User,
  Key,
  CreditCard,
  Download,
  Settings,
  Crown,
  Shield,
  Monitor,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  Copy,
  Eye,
  EyeOff,
  LogOut,
  Trash2,
  Lock,
  ExternalLink,
} from "lucide-react";
import {
  useAuth,
  fetchUserLicenses,
  fetchPurchaseHistory,
  type LicenseInfo,
  type PurchaseHistory,
} from "../lib/auth";

type DashboardTab =
  | "account"
  | "license"
  | "billing"
  | "downloads"
  | "settings";

const TABS: { id: DashboardTab; label: string; icon: React.ReactNode }[] = [
  { id: "account", label: "Account", icon: <User className="w-4 h-4" /> },
  { id: "license", label: "License", icon: <Key className="w-4 h-4" /> },
  { id: "billing", label: "Billing", icon: <CreditCard className="w-4 h-4" /> },
  {
    id: "downloads",
    label: "Downloads",
    icon: <Download className="w-4 h-4" />,
  },
  { id: "settings", label: "Settings", icon: <Settings className="w-4 h-4" /> },
];

const DOWNLOAD_LINKS = {
  windows:
    "https://github.com/ZepioCs/Nexatice-Releases/releases/latest/download/Nexatice-1.0.3-setup.exe",
  releases: "https://github.com/ZepioCs/Nexatice-Releases/releases/tag/v1.0.3",
};

export function Dashboard() {
  const {
    user,
    isLoading: authLoading,
    logout,
    linkLicense,
    changePassword,
    refreshAuth,
  } = useAuth();
  const [activeTab, setActiveTab] = useState<DashboardTab>("account");

  if (authLoading) {
    return (
      <div className="relative min-h-screen">
        <div className="noise-overlay" />
        <MeshGradient />
        <Navbar currentPage="dashboard" />
        <main className="relative z-10 pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    window.history.pushState({}, "", "/login");
    window.dispatchEvent(new PopStateEvent("popstate"));
    return null;
  }

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="dashboard" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="dashboard-header">
            <div className="dashboard-user">
              <div className="dashboard-avatar">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h1>Dashboard</h1>
                <p>{user.email}</p>
              </div>
            </div>
            <div className="dashboard-tier">
              {user.license ? (
                <span className="tier-badge pro">
                  <Crown className="w-4 h-4" />
                  Pro Monthly
                </span>
              ) : (
                <span className="tier-badge free">
                  <Shield className="w-4 h-4" />
                  Free
                </span>
              )}
            </div>
          </div>

          <div className="dashboard-layout">
            <nav className="dashboard-nav">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`dashboard-nav-item ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>

            <div className="dashboard-content">
              {activeTab === "account" && <AccountTab user={user} />}
              {activeTab === "license" && (
                <LicenseTab
                  user={user}
                  linkLicense={linkLicense}
                  refreshAuth={refreshAuth}
                />
              )}
              {activeTab === "billing" && <BillingTab user={user} />}
              {activeTab === "downloads" && <DownloadsTab />}
              {activeTab === "settings" && (
                <SettingsTab logout={logout} changePassword={changePassword} />
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function AccountTab({
  user,
}: {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
}) {
  return (
    <div className="dashboard-section">
      <h2>Account Overview</h2>
      <div className="info-card">
        <div className="info-row">
          <span className="info-label">Email</span>
          <span className="info-value">{user.email}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Account Status</span>
          <span className="info-value status active">
            <CheckCircle className="w-4 h-4" />
            Active
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Subscription</span>
          <span className="info-value">
            {user.subscriptionStatus === "free" && "Free Plan"}
            {user.subscriptionStatus === "pro_monthly" && "Pro Monthly"}
          </span>
        </div>
      </div>

      {!user.license && (
        <div className="upgrade-banner">
          <div className="upgrade-content">
            <Crown className="w-8 h-8 text-amber-400" />
            <div>
              <h3>Upgrade to Pro</h3>
              <p>
                Unlock all features including Performance Advisor, Governance,
                and more
              </p>
            </div>
          </div>
          <a href="/#pricing" className="upgrade-btn">
            View Plans
          </a>
        </div>
      )}
    </div>
  );
}

function LicenseTab({
  user,
  linkLicense,
  refreshAuth,
}: {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  linkLicense: (key: string) => Promise<{ success: boolean; message: string }>;
  refreshAuth: () => Promise<boolean>;
}) {
  const [licenseKey, setLicenseKey] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [licenses, setLicenses] = useState<LicenseInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    setIsLoading(true);
    const result = await fetchUserLicenses();
    if (result.success) {
      setLicenses(result.licenses);
    }
    setIsLoading(false);
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!licenseKey.trim()) {
      setError("Please enter a license key");
      return;
    }

    setIsLinking(true);
    const result = await linkLicense(licenseKey.trim());
    setIsLinking(false);

    if (result.success) {
      setSuccess("License linked successfully!");
      setLicenseKey("");
      await refreshAuth();
      await loadLicenses();
    } else {
      setError(result.message);
    }
  };

  const maskKey = (key: string) => {
    const parts = key.split("-");
    if (parts.length < 5) return key;
    return `${parts[0]}-****-****-****-${parts[4]}`;
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const toggleShowKey = (key: string) => {
    setShowKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (isLoading) {
    return (
      <div className="dashboard-section">
        <h2>License Management</h2>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-section">
      <h2>License Management</h2>

      {licenses.length > 0 ? (
        <div className="licenses-list">
          <h3>Your Licenses ({licenses.length})</h3>
          {licenses.map((license) => (
            <div key={license.key} className="info-card license-card">
              <div className="license-header">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="license-type">Pro Monthly</span>
                <span className={`status-badge ${license.status}`}>
                  {license.status === "active" ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : (
                    <AlertCircle className="w-3 h-3" />
                  )}
                  {license.status}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">License Key</span>
                <div className="info-value license-key">
                  <code>
                    {showKeys[license.key] ? license.key : maskKey(license.key)}
                  </code>
                  <button
                    onClick={() => toggleShowKey(license.key)}
                    className="icon-btn"
                  >
                    {showKeys[license.key] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => copyKey(license.key)}
                    className="icon-btn"
                  >
                    {copiedKey === license.key ? (
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              {license.expiresAt && (
                <div className="info-row">
                  <span className="info-label">Renewal Date</span>
                  <span className="info-value">
                    <Calendar className="w-4 h-4" />
                    {new Date(license.expiresAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="info-row">
                <span className="info-label">Devices</span>
                <span className="info-value">
                  <Monitor className="w-4 h-4" />
                  {license.machineCount} / {license.maxMachines} devices used
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="info-card">
          <p className="text-zinc-400 mb-4">
            You don't have any Pro licenses yet. Enter your license key below to
            activate Pro features. Pro subscriptions will be available in early
            2026.
          </p>
        </div>
      )}

      <div className="license-link-section">
        <h3>
          {licenses.length > 0
            ? "Activate Another License"
            : "Activate License"}
        </h3>
        <form onSubmit={handleLink} className="license-form">
          {error && (
            <div className="form-error">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          {success && (
            <div className="form-success">
              <CheckCircle className="w-4 h-4" />
              {success}
            </div>
          )}
          <div className="license-input-group">
            <input
              type="text"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
              placeholder="NEXA-XXXX-XXXX-XXXX-XXXX"
              disabled={isLinking}
            />
            <button type="submit" disabled={isLinking}>
              {isLinking ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Activate"
              )}
            </button>
          </div>
        </form>
        <p className="license-help">
          <a href="/#pricing" className="text-link">
            <ExternalLink className="w-3 h-3" />
            View Pro features (coming early 2026)
          </a>
        </p>
      </div>
    </div>
  );
}

function BillingTab({
  user,
}: {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
}) {
  const [purchases, setPurchases] = useState<PurchaseHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    setIsLoading(true);
    const result = await fetchPurchaseHistory();
    if (result.success) {
      setPurchases(result.purchases);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="dashboard-section">
        <h2>Billing History</h2>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-section">
      <h2>Billing History</h2>

      {user.license ? (
        <div className="info-card">
          <div className="info-row">
            <span className="info-label">Current Plan</span>
            <span className="info-value">Pro Monthly</span>
          </div>
          {user.license.expiresAt && (
            <div className="info-row">
              <span className="info-label">Next Billing Date</span>
              <span className="info-value">
                {new Date(user.license.expiresAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      ) : null}

      <h3>Purchase History</h3>
      {purchases.length > 0 ? (
        <div className="billing-table">
          <div className="billing-header">
            <span>Date</span>
            <span>Description</span>
            <span>Amount</span>
            <span>Status</span>
          </div>
          {purchases.map((purchase) => (
            <div key={purchase.id} className="billing-row">
              <span>{new Date(purchase.date).toLocaleDateString()}</span>
              <span>{purchase.description}</span>
              <span>€{purchase.amount.toFixed(2)}</span>
              <span className={`status ${purchase.status}`}>
                <CheckCircle className="w-4 h-4" />
                {purchase.status}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="info-card">
          <p className="text-zinc-400">
            No purchase history available. You're currently on the free plan.
          </p>
          <a href="/#pricing" className="upgrade-link">
            <Crown className="w-4 h-4" />
            Pro coming early 2026
          </a>
        </div>
      )}
    </div>
  );
}

function DownloadsTab() {
  return (
    <div className="dashboard-section">
      <h2>Download Nexatice</h2>
      <p className="section-description">
        Download the latest version of Nexatice for your operating system.
      </p>

      <div className="download-cards">
        <a href={DOWNLOAD_LINKS.windows} className="download-card" download>
          <div className="download-icon windows">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
            </svg>
          </div>
          <div className="download-info">
            <h3>Windows</h3>
            <p>Windows 10/11 (64-bit)</p>
          </div>
          <Download className="download-action" />
        </a>

        <div className="download-card disabled">
          <div className="download-icon mac">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
          </div>
          <div className="download-info">
            <h3>macOS</h3>
            <p>Coming Soon</p>
          </div>
          <span className="coming-soon-badge">Soon</span>
        </div>

        <div className="download-card disabled">
          <div className="download-icon linux">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489.117.77.418 1.487.927 2.085.543.64 1.284 1.142 2.065 1.482.26.113.547.2.857.26-.178.21-.351.442-.487.693-.302.558-.457 1.174-.457 1.8 0 .332.04.66.12.965.168.64.51 1.201.98 1.644.469.444 1.072.76 1.745.922.673.16 1.406.169 2.1.012a4.438 4.438 0 0 0 .878-.3c.287-.133.55-.307.786-.508.466-.4.837-.92 1.074-1.51.248-.617.374-1.298.374-1.998v-.09c.27.12.556.21.855.27.673.13 1.382.11 2.04-.06.66-.17 1.26-.49 1.73-.93.47-.44.82-1 .99-1.64.08-.3.12-.63.12-.96 0-.63-.15-1.25-.46-1.8-.14-.25-.31-.48-.49-.69.31-.06.6-.15.86-.26.78-.34 1.52-.84 2.06-1.48.51-.6.81-1.32.93-2.09.12-.8-.01-1.66-.29-2.49-.59-1.77-1.83-3.47-2.72-4.52-.75-1.07-.97-1.93-1.05-3.02-.07-1.49 1.06-5.97-3.17-6.3-.16-.01-.32-.02-.48-.02zm-2.37 9.36c.56 0 1.01.45 1.01 1.01 0 .56-.45 1.01-1.01 1.01-.56 0-1.01-.45-1.01-1.01 0-.56.45-1.01 1.01-1.01zm4.74 0c.56 0 1.01.45 1.01 1.01 0 .56-.45 1.01-1.01 1.01-.56 0-1.01-.45-1.01-1.01 0-.56.45-1.01 1.01-1.01z" />
            </svg>
          </div>
          <div className="download-info">
            <h3>Linux</h3>
            <p>Coming Soon</p>
          </div>
          <span className="coming-soon-badge">Soon</span>
        </div>
      </div>

      <div className="download-links">
        <a
          href={DOWNLOAD_LINKS.releases}
          target="_blank"
          rel="noopener noreferrer"
          className="text-link"
        >
          <ExternalLink className="w-4 h-4" />
          View all releases on GitHub
        </a>
      </div>
    </div>
  );
}

function SettingsTab({
  logout,
  changePassword,
}: {
  logout: () => Promise<void>;
  changePassword: (
    current: string,
    newPass: string
  ) => Promise<{ success: boolean; message: string }>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChanging, setIsChanging] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }

    setIsChanging(true);
    const result = await changePassword(currentPassword, newPassword);
    setIsChanging(false);

    if (result.success) {
      setPasswordSuccess("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } else {
      setPasswordError(result.message);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const handleLogoutAll = async () => {
    setIsLoggingOut(true);
    await logout();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <div className="dashboard-section">
      <h2>Account Settings</h2>

      <div className="settings-card">
        <h3>
          <Lock className="w-4 h-4" />
          Change Password
        </h3>
        <form onSubmit={handleChangePassword} className="settings-form">
          {passwordError && (
            <div className="form-error">
              <AlertCircle className="w-4 h-4" />
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="form-success">
              <CheckCircle className="w-4 h-4" />
              {passwordSuccess}
            </div>
          )}
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isChanging}
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isChanging}
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isChanging}
            />
          </div>
          <button
            type="submit"
            className="settings-btn primary"
            disabled={isChanging}
          >
            {isChanging ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Update Password"
            )}
          </button>
        </form>
      </div>

      <div className="settings-card">
        <h3>
          <LogOut className="w-4 h-4" />
          Sessions
        </h3>
        <p className="settings-description">
          Manage your active sessions across devices.
        </p>
        <div className="settings-actions">
          <button
            className="settings-btn"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Logout"
            )}
          </button>
          <button
            className="settings-btn warning"
            onClick={handleLogoutAll}
            disabled={isLoggingOut}
          >
            Logout All Devices
          </button>
        </div>
      </div>

      <div className="settings-card danger">
        <h3>
          <Trash2 className="w-4 h-4" />
          Danger Zone
        </h3>
        <p className="settings-description">
          Permanently delete your account and all associated data. This action
          cannot be undone.
        </p>
        {showDeleteConfirm ? (
          <div className="delete-confirm">
            <p>Are you sure? This will permanently delete your account.</p>
            <div className="settings-actions">
              <button
                className="settings-btn"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button className="settings-btn danger">Delete My Account</button>
            </div>
          </div>
        ) : (
          <button
            className="settings-btn danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Account
          </button>
        )}
      </div>
    </div>
  );
}
